import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useAppSelector } from "module/main/store/hooks";
import * as Sentry from "@sentry/react";
import { Button } from "module/common/components/Buttons";
import SideNav from "../HomePage/components/SideNav/SideNav";
import Person from "./components/Person";
import Properties from "./components/Properties";
import APNProperties from "./components/APNProperties";
import Business from "./components/Business";
import PropertyDetails from "./components/PropertyDetails";
import ReversePhone from "./components/ReversePhone";
import Workplace from "./components/Workplace";
import Criminal from "./components/Criminal";
import DNC from "./components/DNC";
import { selectApiKeys, selectUserInfo } from "module/main/store/auth/selectors";
import { useAppDispatch } from "module/main/store/hooks";
import { callGetApiKeys } from "module/main/store/auth/thunk";
import { addErrorToast } from "module/main/store/toast/reducer";
var tabs = [
    { id: "properties", name: "Property Lookup" },
    { id: "apn-properties", name: "APN Property Lookup" },
    { id: "property-details", name: "Property Details" },
    { id: "person", name: "Person Lookup" },
    { id: "reverse-phone", name: "Reverse Phone" },
    { id: "workplace", name: "Workplace Lookup" },
    { id: "criminal", name: "Criminal Check" },
    { id: "business", name: "Business Lookup" },
    { id: "dnc", name: "DNC Status" },
];
var EndpointsForms = function () {
    var dispatch = useAppDispatch();
    var apiKeys = useSelector(selectApiKeys);
    var userInfo = useAppSelector(selectUserInfo);
    var _a = useState(false), isFormLoading = _a[0], setFormLoading = _a[1];
    var _b = useState(null), formResponse = _b[0], setFormResponse = _b[1];
    var _c = useState("properties"), activeTab = _c[0], setActiveTab = _c[1];
    var _d = useState(""), selectedKey = _d[0], setSelectedKey = _d[1];
    var responseRef = useRef(null);
    var topRef = useRef(null);
    var formRef = useRef(null);
    useEffect(function () {
        if (formResponse && responseRef.current) {
            responseRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [formResponse]);
    var apiKeyOptions = useMemo(function () {
        if (!apiKeys)
            return [];
        return apiKeys.map(function (key) { return ({
            value: key.id,
            label: "".concat(key.keyType, " - ").concat(key.id.substring(0, 8), " (").concat(key.triesLeft, " tries left)"),
        }); });
    }, [apiKeys]);
    var Component = useMemo(function () {
        switch (activeTab) {
            case "properties":
                return Properties;
            case "apn-properties":
                return APNProperties;
            case "property-details":
                return PropertyDetails;
            case "person":
                return Person;
            case "reverse-phone":
                return ReversePhone;
            case "workplace":
                return Workplace;
            case "criminal":
                return Criminal;
            case "business":
                return Business;
            case "dnc":
                return DNC;
            default:
                return Properties;
        }
    }, [activeTab]);
    var goToTop = function () {
        var _a;
        (_a = topRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({ behavior: "smooth" });
    };
    var clearForm = function () {
        var _a;
        (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.reset();
        setFormResponse(null);
    };
    var formSubmitCallBack = function (apiFunc, curatedPayload) {
        if (!selectedKey) {
            dispatch(addErrorToast("Please select an API key"));
            return;
        }
        setFormLoading(true);
        apiFunc(curatedPayload, selectedKey)
            .then(function (_a) {
            var data = _a.data;
            return setFormResponse(JSON.stringify(data, null, 2));
        })
            .catch(function (error) { return setFormResponse(JSON.stringify(error.response.data, null, 2)); })
            .finally(function () {
            setFormLoading(false);
            dispatch(callGetApiKeys());
            Sentry.captureMessage("".concat(userInfo === null || userInfo === void 0 ? void 0 : userInfo.email, " used ").concat(activeTab, " endpoint on Frontend App!"));
        });
    };
    return (_jsxs("section", { className: "flex h-full bg-appBg", "data-testid": "endpoints-forms", children: [_jsx(SideNav, {}), _jsx("div", { className: "min-w-0 flex-1 overflow-auto", children: _jsxs("div", { className: "mx-auto w-full max-w-7xl px-8 py-8", ref: topRef, children: [_jsxs("div", { className: "mb-6 border-b border-sherpaChocoCream pb-5", children: [_jsx("p", { className: "mb-2 text-xs font-extrabold uppercase tracking-[0.18em] text-primary", children: "Try API" }), _jsx("h1", { className: "text-3xl font-extrabold text-ink", children: "Endpoint playground" }), _jsx("p", { className: "mt-2 text-sm text-mutedBlue", children: "Pick an API key, test an endpoint, and inspect the JSON response." })] }), _jsxs("div", { className: "rounded-2xl border border-sherpaChocoCream bg-white shadow-sm", children: [_jsx("div", { className: "border-b border-sherpaChocoCream px-6 py-4", children: _jsxs("label", { className: "block max-w-md", children: [_jsx("span", { className: "text-xs font-extrabold uppercase tracking-[0.16em] text-slate", children: "Endpoint" }), _jsx("select", { className: "mt-2 w-full rounded-xl border border-sherpaChocoCream bg-white px-4 py-3 text-sm font-bold text-ink", value: activeTab, onChange: function (event) { return setActiveTab(event.target.value); }, children: tabs.map(function (tab) { return (_jsx("option", { value: tab.id, children: tab.name }, tab.id)); }) })] }) }), _jsxs("div", { className: "p-6", children: [_jsxs("div", { className: "mb-6 grid gap-4 rounded-2xl bg-sherpaTeaCream p-4 lg:grid-cols-[1fr_auto_auto] lg:items-end", children: [_jsxs("label", { className: "block", children: [_jsx("span", { className: "text-xs font-extrabold uppercase tracking-[0.16em] text-slate", children: "API key" }), _jsxs("select", { className: "mt-2 w-full rounded-xl border border-sherpaChocoCream bg-white px-4 py-3 text-sm font-bold text-ink", value: selectedKey, onChange: function (event) { return setSelectedKey(event.target.value); }, children: [_jsx("option", { value: "", children: "Choose an option" }), apiKeyOptions.map(function (option) { return (_jsx("option", { value: option.value, children: option.label }, option.value)); })] })] }), _jsx(Button, { type: "submit", form: "endpoints-form", disabled: isFormLoading, children: isFormLoading ? "Submitting…" : "Submit form" }), _jsx(Button, { type: "button", variant: "outline", onClick: clearForm, children: "Clear form" })] }), _jsx("div", { className: "endpoint-form-shell", children: _jsx(Component, { formSubmitCallBack: formSubmitCallBack, formRef: formRef }) }), formResponse ? (_jsxs("div", { className: "mt-8", ref: responseRef, children: [_jsxs("div", { className: "mb-3 flex items-center justify-between gap-3", children: [_jsx("h2", { className: "text-xl font-extrabold text-ink", children: "Result" }), _jsxs("div", { className: "flex flex-wrap gap-2", children: [_jsx(Button, { type: "button", variant: "ghost", onClick: goToTop, children: "Go to top" }), _jsx(Button, { type: "button", variant: "outline", onClick: function () {
                                                                        var blob = new Blob([formResponse], { type: "application/json" });
                                                                        var url = URL.createObjectURL(blob);
                                                                        var fileHash = Math.random().toString(36).substring(7);
                                                                        var anchor = document.createElement("a");
                                                                        anchor.href = url;
                                                                        anchor.download = "result-".concat(activeTab, "-").concat(fileHash, ".json");
                                                                        anchor.click();
                                                                        URL.revokeObjectURL(url);
                                                                    }, children: "Download result" })] })] }), _jsx("pre", { className: "max-h-[520px] overflow-auto rounded-2xl bg-slate-950 p-5 text-sm shadow-inner", children: formResponse })] })) : null] })] })] }) })] }));
};
export default EndpointsForms;
